import {
  MimeTypes,
  RENT_MATRIX_PAGE_SIZE,
  rentMatrix as rentMatrixQuery,
  rentMatrixExport as rentMatrixExportQuery,
  rentMatrixFilterOptions as rentMatrixFilterOptionsQuery,
  rentMatrixRows as rentMatrixRowsQuery,
  type RentExportStatus,
  type RentMatrixExport,
  type RentMatrixExportQueryVariables,
  type RentMatrixFilterOptions,
  type RentMatrixFilterOptionsQueryVariables,
  type RentMatrixPage,
  type RentMatrixQueryVariables,
  type RentMatrixRows,
  type RentMatrixRowsQueryVariables,
  type RentRow,
} from '@rentguru/commons-utils';
import { query } from '@up2rent/graphql';
import isNil from 'lodash/isNil';

import { gunzipBase64 } from './rentTrackingUtils';

const CSV_MIME_TYPE = `${MimeTypes.CSV};charset=utf-8`;

interface RentMatrixCsvFile {
  file: Blob | null;
  fileName: string;
  status: RentExportStatus;
}

export const fetchRentMatrix = (variables: RentMatrixQueryVariables, signal?: AbortSignal): Promise<RentMatrixPage> =>
  query<RentMatrixPage, RentMatrixQueryVariables>(rentMatrixQuery, variables, signal);

export const fetchRentMatrixPagesWhile = async (
  variables: Omit<RentMatrixQueryVariables, 'page'>,
  continues: (page: RentMatrixPage) => boolean,
  signal?: AbortSignal,
  offset = 0
): Promise<RentRow[]> => {
  const page = await fetchRentMatrix({ ...variables, page: { limit: RENT_MATRIX_PAGE_SIZE, offset } }, signal);
  if (!page.pageInfo.hasMore || !continues(page)) {
    return page.rows;
  }
  const nextRows = await fetchRentMatrixPagesWhile(variables, continues, signal, offset + RENT_MATRIX_PAGE_SIZE);
  return [...page.rows, ...nextRows];
};

export const fetchRentMatrixRows = (
  variables: RentMatrixRowsQueryVariables,
  signal?: AbortSignal
): Promise<RentMatrixRows> =>
  query<RentMatrixRows, RentMatrixRowsQueryVariables>(rentMatrixRowsQuery, variables, signal);

export const fetchRentMatrixFilterOptions = (
  variables: RentMatrixFilterOptionsQueryVariables,
  signal?: AbortSignal
): Promise<RentMatrixFilterOptions> =>
  query<RentMatrixFilterOptions, RentMatrixFilterOptionsQueryVariables>(
    rentMatrixFilterOptionsQuery,
    variables,
    signal
  );

export const fetchRentMatrixExport = async (
  variables: RentMatrixExportQueryVariables,
  signal?: AbortSignal
): Promise<RentMatrixCsvFile> => {
  const { content, fileName, status } = await query<RentMatrixExport, RentMatrixExportQueryVariables>(
    rentMatrixExportQuery,
    variables,
    signal
  );
  return { file: isNil(content) ? null : await gunzipBase64(content, CSV_MIME_TYPE), fileName, status };
};
